import styles from "./CTAForm.module.css";
import { FaCheck, FaGraduationCap, FaUsers, FaGlobe, FaTimes } from "react-icons/fa";
import { useForm } from "../../context/FormContext";

const CTAForm = ({ isPopup = false }) => {
  const { isFormOpen, closeForm } = useForm();

  // If it's a popup and not open, don't render anything
  if (isPopup && !isFormOpen) return null;

  const benefits = {
    "Why Choose STEMx?": [
      "NEP 2020 aligned curriculum",
      "Partnership with Japanese education leader KTC Holdings",
      "240+ schools trust us across India",
      "STEM.org USA accredited programs",
      "Complete lab setup and training",
    ],
    "What We Deliver": [
      "AI & Robotics",
      "STEM Lab",
      "Maker Space Labs",
      "Atal Tinkering Labs",
      "Japanese Exchange Program",
      "STEMx Impact program",
    ],
    "Student Benefits": [
      "Hands-on learning",
      "Critical thinking skills",
      "Future-ready capabilities",
      "Global exposure",
      "Competition opportunities",
    ],
  };
  const designationOptions = [
    "Chairman",
    "Principal",
    "Teacher",
    "MD",
    "Channel Partners",
  ];

  const handleSubmit = (e) => {
    e.preventDefault();

    try {
      // Get form elements
      const form = e.target;

      // Get selected demo types with null check
      const demoCheckboxes = form.querySelectorAll('input[name="demo-type"]');
      const selectedDemos = Array.from(demoCheckboxes)
        .filter((checkbox) => checkbox && checkbox.checked)
        .map((checkbox) => checkbox.parentElement?.textContent?.trim())
        .filter(Boolean)
        .join(", ");

      // Safely get form values with null checks
      const getFormValue = (name) => {
        const element = form.elements[name];
        return element ? element.value || "" : "";
      };

      // Construct email body
      const emailBody = `
Name: ${getFormValue("fullName")}
Email: ${getFormValue("email")}
Phone: ${getFormValue("phone")}
Designation: ${getFormValue("designation")}
School/Organisation: ${getFormValue("organization")}
Country: ${getFormValue("country")}
State: ${getFormValue("state")}
Selected Demos: ${selectedDemos}
Message: ${getFormValue("message")}
      `.trim();

      // Construct mailto URL
      const mailtoUrl = `mailto:info@stemxindia.com?subject=Demo Request from ${getFormValue(
        "organization"
      )}&body=${encodeURIComponent(emailBody)}`;

      // Open default email client
      window.location.href = mailtoUrl;
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("There was an error submitting the form. Please try again.");
    }
  };

  const content = (
    <div className={styles.container}>
      <div className={styles.content}>
        <h2 className={styles.title} data-aos="fade-up">
          Transform Your School's Future
        </h2>
        <p
          className={styles.subtitle}
          data-aos="fade-up"
          data-aos-delay="100"
        >
          World-class STEM education powered by international partnerships and
          proven expertise.
        </p>

        <div className={styles.benefitsGrid}>
          {Object.entries(benefits).map(
            ([category, items], categoryIndex) => (
              <div
                key={category}
                className={styles.benefitSection}
                data-aos="fade-up"
                data-aos-delay={categoryIndex * 100}
              >
                <h3 className={styles.benefitTitle}>{category}</h3>
                <ul className={styles.benefitList}>
                  {items.map((item, index) => (
                    <li key={index}>
                      <FaCheck className={styles.checkIcon} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )
          )}
        </div>

        <p className={styles.joinText} data-aos="fade-up">
          Join 240+ leading schools in revolutionizing STEM education
        </p>
      </div>

      <div className={styles.formContainer} data-aos="fade-left">
        <form className={styles.form} onSubmit={handleSubmit}>
          <h3>Request your Demo Today</h3>

          <div className={styles.formField}>
            <label>
              Full Name <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              name="fullName"
              placeholder="Please Enter Name"
              required
            />
          </div>

          <div className={styles.formField}>
            <label>
              Email <span className={styles.required}>*</span>
            </label>
            <input type="email" name="email" placeholder="Email" required />
          </div>

          <div className={styles.formField}>
            <label>
              Phone Number <span className={styles.required}>*</span>
            </label>
            <input
              type="tel"
              name="phone"
              placeholder="Country/area code, for example: +1 987 654 3210"
              required
            />
          </div>

          <div className={styles.formField}>
            <label>
              Designation <span className={styles.required}>*</span>
            </label>
            <select name="designation" required>
              {designationOptions.map((option) => (
                <option key={option} value={option.toLowerCase()}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formField}>
            <label>
              School / Organisation <span className={styles.required}>*</span>
            </label>
            <input type="text" name="organization" required />
          </div>

          <div className={styles.formField}>
            <label>
              Country <span className={styles.required}>*</span>
            </label>
            <input type="text" name="country" required />
          </div>

          <div className={styles.formField}>
            <label>
              State <span className={styles.required}>*</span>
            </label>
            <input type="text" name="state" required />
          </div>

          <div className={styles.formField}>
            <label>
              Schedule demo for (check all that apply){" "}
              <span className={styles.required}>*</span>
            </label>
            <div className={styles.checkboxGroup}>
              <label>
                <input
                  type="checkbox"
                  name="demo-type"
                  value="ai-robotics-lab"
                />
                AI and Robotics Lab
              </label>
              <label>
                <input
                  type="checkbox"
                  name="demo-type"
                  value="atal-tinkering-lab"
                />
                STEM Lab
              </label>
              <label>
                <input
                  type="checkbox"
                  name="demo-type"
                  value="coding-ai-curriculum"
                />
                Maker Space Labs
              </label>
              <label>
                <input
                  type="checkbox"
                  name="demo-type"
                  value="hardware-kits"
                />
                Japanese Exchange Program
              </label>
              <label>
                <input
                  type="checkbox"
                  name="demo-type"
                  value="StemX Impact program"
                />
                STEMx Impact program
              </label>
            </div>
          </div>

          <div className={styles.formField}>
            <label>Your Message</label>
            <textarea
              name="message"
              placeholder="Please add as much information as possible to ensure we can fully support you!"
              rows="4"
            ></textarea>
          </div>

          <button type="submit" className={styles.submitButton}>
            Submit Demo Request
          </button>
        </form>
      </div>
    </div>
  );

  if (isPopup) {
    return (
      <div className={styles.popup}>
        <div className={styles.popupContent}>
          <button className={styles.closeButton} onClick={closeForm}>
            <FaTimes />
          </button>
          {content}
        </div>
      </div>
    );
  }

  return <section className={styles.cta} id="cta-form">{content}</section>;
};

export default CTAForm;
